import { ApiStatus } from 'Constants';
import jwtDecode from 'jwt-decode';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { INITIALIZATION_ERROR } from 'Reducers/nav/types';
import { persistAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';
import { updateConfig } from 'Services/config';
import { getPatient } from 'Services/patient';
import { waitForToken } from './appBridge';
const root = async (pageCtx) => {
    const queryParams = pageCtx.query;
    const { country, app: appParam, appVersion, deviceManufacturer, deviceModel, osType, osVersion, product, } = queryParams || {};
    let compatibility = true;
    if (pageCtx.pathname === '/reset-password' || pageCtx.pathname === '/verify-email') {
        compatibility = false;
    }
    const app = appParam || product;
    try {
        await updateConfig(compatibility, country, app, appVersion, deviceManufacturer, deviceModel, osType, osVersion);
    }
    catch (err) {
        const error = err;
        setupInitializationErrorLogic(error.status);
    }
};
const setupInitializationErrorLogic = (errorStatus = 404) => {
    if (store) {
        store.dispatch({ type: INITIALIZATION_ERROR, initErrorStatus: errorStatus });
    }
};
const setupProtectedPageInitialization = async (authSession, queryParams, redirectedCountry) => {
    const { app, country: countryParam, appVersion, deviceManufacturer, deviceModel, osType, osVersion, } = queryParams;
    const country = redirectedCountry ?? countryParam;
    await updateConfig(true, country, app, appVersion, deviceManufacturer, deviceModel, osType, osVersion);
    const patient = await getPatient(authSession.account_id, osType, osVersion, app, appVersion);
    store.dispatch({
        type: AUTH_SESSION,
        authSession: { ...authSession, include: { patient } },
    });
};
const createAuthSession = (token) => {
    try {
        const decodedToken = jwtDecode(token);
        return {
            access_token: token,
            account_id: decodedToken.id,
            expires: decodedToken.exp,
        };
    }
    catch {
        throw { status: 401, message: 'Invalid token' };
    }
};
const handleWrongRegionError = async (error, authSession, queryParams) => {
    const unsupportedCountry = error.response?.data.errors[0].unsupportedCountry;
    if (!unsupportedCountry) {
        throw error;
    }
    try {
        await setupProtectedPageInitialization(authSession, queryParams, unsupportedCountry);
    }
    catch (err) {
        const error = err;
        setupInitializationErrorLogic(error.status);
    }
};
const protectedPageAction = async (pageCtx) => {
    const queryParams = pageCtx.query;
    if (!queryParams) {
        setupInitializationErrorLogic(ApiStatus.BAD_REQUEST);
        return;
    }
    try {
        const token = await waitForToken(queryParams.token);
        const authSession = createAuthSession(token);
        persistAuthSession(authSession);
        try {
            await setupProtectedPageInitialization(authSession, queryParams);
        }
        catch (err) {
            const error = err;
            if (error.response?.data.code === ApiStatus.WRONG_REGION) {
                await handleWrongRegionError(error, authSession, queryParams);
                return;
            }
            setupInitializationErrorLogic(error.status);
        }
    }
    catch (err) {
        const error = err;
        setupInitializationErrorLogic(error.status);
    }
};
// Create your paths that you want to handle here
// you want to most specific earlier and the least specific later
// Note: if one action throws an error (calls promise reject) then the handlePaths
// will move onto the next matching route
const routeActions = [
    {
        regex: [
            /^\/account-information/i,
            /^\/update-password/i,
            /^\/rwe/i,
            /^\/hipaa/i,
            /^\/consents/i,
            /^\/notices-consents/i,
            /^\/assist/i,
        ],
        action: protectedPageAction,
    },
    {
        regex: [/^\.*/],
        action: root,
    },
];
export const handlePaths = (ctx) => {
    const doActions = [];
    for (const check of routeActions) {
        for (const r of check.regex) {
            if (ctx.canonicalPath.match(r)) {
                // Add valid actions to chain later
                doActions.push(check.action);
            }
        }
    }
    // Start with a rejected promise
    let p = Promise.reject();
    // Build a negative promise chain that will try the next action if the previous one failed
    // Once an action succeeds the chain ends and the app starts
    for (let i = 0; i < doActions.length; i++) {
        p = p.catch(() => doActions[i](ctx));
    }
    return p;
};
